import Logo from "@/assets/img/soulmate/logo.png"
import PriceArrow from "@/assets/img/soulmate/price-arrow.svg?react"
import PriceBg from "@/assets/img/soulmate/checkout/price-bg.png"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { MobileLayout } from "@/shared/components/mobile-layout"
import type { StepComponentProps } from "./step-contract"

/** "Pay-what-feels-right" amounts (Figma Price screen). $5 is flagged POPULAR. */
const PRICE_TILES = [
  { label: "$1", value: "1" },
  { label: "$5", value: "5", popular: true },
  { label: "$9", value: "9" },
  { label: "$13.67", value: "13.67" },
]

/**
 * Full-bleed "choose a price that feels right" step (Figma LPR_Onboarding_Choose_
 * Price). A pay-what-you-want amount picker shown after the loading screen; the
 * tiles are the design's UX, not the actual charge. The picked amount is stored
 * in the answers (analytics only); "See my sketch" advances, which completes the
 * quiz and routes to the checkout paywall. Wired as a per-step `component`.
 */
export function SoulmatePriceStep({ value, onChange, onAdvance }: StepComponentProps) {
  const picked = typeof value === "string" ? value : null

  return (
    <div className="relative flex min-h-full flex-1 flex-col bg-dark-base font-body text-purple-light">
      <img
        src={PriceBg}
        alt=""
        className="pointer-events-none absolute inset-x-0 top-0 h-[240px] w-full object-cover"
      />

      <MobileLayout className="relative flex flex-1 flex-col px-4 pt-10 pb-6">
        <div className="flex flex-col items-center gap-6">
          <img src={Logo} alt="" className="size-22 rounded-[25px]" />
          <h1 className="text-center font-display text-2xl font-medium text-purple-light">
            Choose a price that feels right for you
          </h1>
          <p className="text-center text-base text-purple-light/70">
            We've prepared your personalized{" "}
            <span className="font-semibold text-white">soulmate sketch</span> and{" "}
            <span className="font-semibold text-white">compatibility insights</span> — including who
            they are, how they love, and the signs that may bring your paths together.
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-2 rounded-[24px] bg-dark-surface p-4">
          <p className="text-center text-base text-purple-light">
            While the actual cost to us is $13.67, we encourage you to select an amount you feel
            comfortable with
          </p>
          <div className="flex items-end justify-center gap-2">
            {PRICE_TILES.map((tile) => {
              const selected = picked === tile.value
              return (
                <button
                  key={tile.value}
                  type="button"
                  onClick={() => onChange(tile.value)}
                  className={cn(
                    "relative flex flex-1 items-center justify-center rounded-lg border-2 py-4 text-base font-semibold text-white transition-colors",
                    selected
                      ? "border-main-purple bg-main-purple/10"
                      : "border-transparent bg-purple-light/10",
                  )}
                >
                  {tile.popular && (
                    <span className="-top-2 absolute rounded-xl bg-green px-2 pt-0.5 pb-px text-[8px] font-bold uppercase text-dark-base">
                      Popular
                    </span>
                  )}
                  {tile.label}
                </button>
              )
            })}
          </div>
        </div>

        <div className="mt-3 flex items-center justify-end gap-4 pr-[50px]">
          <p className="max-w-[252px] text-right text-xs text-purple-light/40">
            Choosing this option helps us support people who need access at a lower price
          </p>
          <PriceArrow className="h-12 w-[52px] shrink-0 text-main-purple" />
        </div>

        <Button
          variant="gradient"
          size="xl"
          className="mt-auto"
          disabled={!picked}
          onClick={onAdvance}
        >
          See my sketch
        </Button>
      </MobileLayout>
    </div>
  )
}
